import React, { createContext, useContext, useEffect, useState } from "react";
import type { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userRole: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    name: string,
    phone: string,
    socialMedia: string,
    coupon: string,
    partnerType?: string
  ) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Helper to check if session is expired
  const isSessionExpired = (session: Session | null): boolean => {
    if (!session || !session.expires_at) return true;
    const expiresAt = session.expires_at * 1000; // Convert to milliseconds
    const now = Date.now();
    // Add 5 minute buffer to account for clock skew and refresh timing
    return now >= expiresAt - 5 * 60 * 1000;
  };

  // Helper to validate and refresh session if needed
  const validateSession = async (currentSession: Session | null): Promise<Session | null> => {
    if (!currentSession) return null;

    // Check if session is expired
    if (isSessionExpired(currentSession)) {
      console.log("Session expired, attempting refresh...");
      try {
        const { data: { session: refreshedSession }, error } = await supabase.auth.refreshSession(currentSession);
        if (error || !refreshedSession || isSessionExpired(refreshedSession)) {
          console.log("Session refresh failed or still expired, clearing session");
          await supabase.auth.signOut();
          return null;
        }
        return refreshedSession;
      } catch (error) {
        console.error("Error refreshing session:", error);
        await supabase.auth.signOut();
        return null;
      }
    }

    return currentSession;
  };

  // Helper to fetch role from influencers table
  const fetchUserRole = async (userId: string) => {
    console.log("Fetching role for user:", userId);
    const { data, error } = await supabase.from("influencers").select("role").eq("id", userId).single();
    if (error) {
      console.error("Error fetching role:", error);
      setUserRole(null);
      return;
    }
    console.log("Role fetched:", data?.role);
    setUserRole(data?.role || null);
  };

  useEffect(() => {
    // Verificar sessão atual
    supabase.auth.getSession().then(async ({ data: { session: initialSession } }) => {
      const validSession = await validateSession(initialSession);
      setSession(validSession);
      setUser(validSession?.user ?? null);
      if (validSession?.user?.id) {
        fetchUserRole(validSession.user.id);
      } else {
        setUserRole(null);
      }
      setLoading(false);
    });

    // Escutar mudanças de autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Handle token refresh events
      if (event === "TOKEN_REFRESHED" || event === "SIGNED_IN") {
        const validSession = await validateSession(session);
        setSession(validSession);
        setUser(validSession?.user ?? null);
        if (validSession?.user?.id) {
          fetchUserRole(validSession.user.id);
        } else {
          setUserRole(null);
        }
      } else if (event === "SIGNED_OUT" || event === "USER_UPDATED") {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user?.id) {
          // Add a small delay to ensure the influencer record is created
          setTimeout(() => {
            fetchUserRole(session.user.id);
          }, 1000);
        } else {
          setUserRole(null);
        }
      } else {
        // For other events, validate session
        const validSession = await validateSession(session);
        setSession(validSession);
        setUser(validSession?.user ?? null);
        if (validSession?.user?.id) {
          fetchUserRole(validSession.user.id);
        } else {
          setUserRole(null);
        }
      }
      setLoading(false);
    });

    // Periodic session validation (check every 5 minutes)
    const intervalId = setInterval(async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession) {
        const validSession = await validateSession(currentSession);
        if (!validSession) {
          // Session expired and couldn't be refreshed, clear state
          setSession(null);
          setUser(null);
          setUserRole(null);
        } else if (validSession !== currentSession) {
          // Session was refreshed, update state
          setSession(validSession);
          setUser(validSession.user);
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => {
      subscription.unsubscribe();
      clearInterval(intervalId);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signUp = async (
    email: string,
    password: string,
    name: string,
    phone: string,
    socialMedia: string,
    coupon: string,
    partnerType: string = "influencer"
  ) => {
    // Criar usuário no Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
        },
      },
    });

    if (authError) throw authError;

    if (authData.user) {
      // Upsert perfil do influenciador no banco
      const normalizedCoupon = (coupon || "").trim().toUpperCase();
      const { error: profileError } = await supabase.from("influencers").upsert({
        id: authData.user.id,
        email,
        name,
        phone,
        social_media: socialMedia,
        coupon: normalizedCoupon,
        role: "user",
        partner_type: partnerType,
      });

      if (profileError) {
        console.error("Profile creation error:", profileError);
        throw new Error("Erro ao criar perfil do influenciador");
      }

      // Immediately fetch the user role after creating the profile
      await fetchUserRole(authData.user.id);
    }
  };

  const signOut = async () => {
    try {
      // Always attempt to sign out, but don't throw if already signed out
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.warn("Sign out error (may already be signed out):", error);
        // Don't throw - we'll clear local state anyway
      }
      // Clear local state regardless of error
      setSession(null);
      setUser(null);
      setUserRole(null);
    } catch (error) {
      console.error("Unexpected sign out error:", error);
      // Still clear local state even if there's an error
      setSession(null);
      setUser(null);
      setUserRole(null);
    }
  };

  const resetPassword = async (email: string) => {
    // Use production URL from env if available, otherwise use current origin (for dev)
    // Set VITE_APP_URL in your production environment (e.g., Vercel env vars)
    const productionUrl = import.meta.env.VITE_APP_URL;
    const origin = productionUrl || window.location.origin;
    const redirectUrl = `${origin}/reset-password`;
    
    console.log("Password reset redirect URL:", redirectUrl);
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) throw error;
  };

  const value = {
    user,
    session,
    loading,
    userRole,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
